import * as React from "react"
import { motion } from "motion/react"
import {
  Flame,
  Spade,
  LayoutDashboard,
  TrendingUp,
  Users,
  Building2,
  MessageSquare,
  Coins,
  CalendarDays,
  Store,
  LifeBuoy,
  Settings,
  Shield,
  LogOut,
  ArrowLeft,
  Menu,
  X,
  Send,
  Banknote,
} from "lucide-react"
import { useAuth } from "@/contexts/AuthContext"
import type { UserRole } from "@/contexts/AuthContext"

export type AdminTab = "overview" | "streaks" | "deals" | "agents" | "agencies" | "chat" | "events" | "economics" | "poker" | "airdrop" | "redemptions" | "store" | "support" | "settings"

const navItems: Array<{ id: AdminTab; label: string; icon: typeof LayoutDashboard }> = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "streaks", label: "Streaks", icon: Flame },
  { id: "deals", label: "Deal Analytics", icon: TrendingUp },
  { id: "agents", label: "Agents", icon: Users },
  { id: "agencies", label: "Agencies", icon: Building2 },
  { id: "chat", label: "Chat Activity", icon: MessageSquare },
  { id: "events", label: "Events", icon: CalendarDays },
  { id: "economics", label: "Economics", icon: Coins },
  { id: "poker", label: "Poker Room", icon: Spade },
  { id: "airdrop", label: "Airdrop", icon: Send },
  { id: "redemptions", label: "Redemptions", icon: Banknote },
  { id: "store", label: "Store", icon: Store },
  { id: "support", label: "Support", icon: LifeBuoy },
  { id: "settings", label: "Settings", icon: Settings },
]

interface AdminSidebarProps {
  activeTab: AdminTab
  onTabChange: (tab: AdminTab) => void
  role: UserRole | null
}

export function AdminSidebar({ activeTab, onTabChange, role }: AdminSidebarProps) {
  const { user, logout } = useAuth()
  const [mobileOpen, setMobileOpen] = React.useState(false)

  const sidebarContent = (
    <>
      {/* Header */}
      <div className="p-5 border-b border-[#2A2A45]">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-[#F37020]/15 flex items-center justify-center">
            <Shield className="w-5 h-5 text-[#F37020]" />
          </div>
          <div>
            <h1 className="text-[15px] font-semibold text-white font-admin">Oval Admin</h1>
            <p className="text-[11px] text-white/30 font-admin">Command Center</p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 min-h-0 overflow-y-auto p-3 space-y-1">
        {navItems.map((item) => {
          const isActive = activeTab === item.id
          return (
            <button
              key={item.id}
              onClick={() => {
                onTabChange(item.id)
                setMobileOpen(false)
              }}
              className={`
                relative w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors
                ${isActive
                  ? "text-white bg-white/[0.06]"
                  : "text-white/40 hover:text-white/70 hover:bg-white/[0.03]"
                }
              `}
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              {isActive && (
                <motion.div
                  layoutId="adminActiveTab"
                  className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 rounded-r-full bg-[#F37020]"
                  transition={{ type: "spring", bounce: 0.2, duration: 0.4 }}
                />
              )}
              <item.icon className={`w-[18px] h-[18px] ${isActive ? "text-[#F37020]" : ""}`} />
              <span>{item.label}</span>
            </button>
          )
        })}
      </nav>

      {/* Footer */}
      <div className="p-4 border-t border-[#2A2A45] space-y-3">
        <div className="px-3 py-2">
          <p className="text-[13px] text-white/70 font-medium truncate font-admin">{user?.email}</p>
          <span className="inline-block mt-1 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#F37020]/15 text-[#F37020]">
            {role === "super_admin" ? "Super Admin" : "Support"}
          </span>
        </div>
        <a
          href="/dashboard"
          className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-white/40 hover:text-white/70 hover:bg-white/[0.03] transition-colors font-admin"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Dashboard
        </a>
        <button
          onClick={logout}
          className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-white/40 hover:text-red-400 hover:bg-red-400/[0.06] transition-colors font-admin"
        >
          <LogOut className="w-4 h-4" />
          Sign Out
        </button>
      </div>
    </>
  )

  return (
    <>
      {/* Mobile trigger */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-4 py-3 bg-[#0C0C12]/95 backdrop-blur-sm border-b border-[#2A2A45]">
        <div className="flex items-center gap-2">
          <Shield className="w-5 h-5 text-[#F37020]" />
          <span className="text-sm font-semibold text-white font-admin">Oval Admin</span>
        </div>
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className="w-9 h-9 rounded-lg bg-white/[0.06] flex items-center justify-center text-white/60"
        >
          {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 z-40 bg-black/60"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Mobile drawer */}
      <div
        className={`
          lg:hidden fixed top-0 left-0 bottom-0 z-40 h-dvh w-64 bg-[#0C0C12] border-r border-[#2A2A45]
          transform transition-transform duration-300
          ${mobileOpen ? "translate-x-0" : "-translate-x-full"}
          flex flex-col overflow-hidden pt-14
        `}
      >
        {sidebarContent}
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:flex h-dvh min-h-0 w-60 flex-shrink-0 bg-[#0C0C12] border-r border-[#2A2A45] flex-col overflow-hidden">
        {sidebarContent}
      </div>
    </>
  )
}
